import React, { useCallback, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DollarSign } from "lucide-react";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useForm } from "react-hook-form";
import {
  ProductItemPriceInfo,
  ProductItemsPriceInfo,
  initialValuesOfProductItemPrice,
} from "../../../redux/slice/product/createProductSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import dayjs, { Dayjs } from "dayjs";
import { Cancel, EditOutlined, ErrorOutlineRounded } from "@mui/icons-material";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import CustomTable from "../../common/CustomTable";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

interface ProductItemsPricePriceProps {
  open: boolean;
  onClose: () => void;
  addProductItemsPrice: (productItemPrice: any) => void;
  productItemPriceObject: ProductItemsPriceInfo;
  editPricing: boolean;
  editProduct: boolean;
  rowIndex: number | null;
  inactiveOnly: boolean;
  productItemPriceList: ProductItemPriceInfo[];
  productItemsList: any;
}

type Column = {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
};

let erroredFields: string[] = [];

const ProductItemsPricePopup: React.FC<ProductItemsPricePriceProps> = ({
  open,
  onClose,
  addProductItemsPrice,
  productItemPriceObject,
  editPricing,
  editProduct,
  rowIndex,
  inactiveOnly,
  productItemPriceList,
  productItemsList,
}) => {
  const showInlineErrorMinLength: number = 60;
  const customTopErrorString: string = "-custom top error";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    inputFieldSx,
    dropdownWrapSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
    isDark,
  } = useModalStyles();

  const modalTableSx = {
    width: "100%",
    border: `1px solid ${tokens.border}`,
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.cardBg,
    "& > div": { mt: 0 },
    "& .MuiTableContainer-root": { borderRadius: 0 },
    "& .MuiTable-root": { background: "transparent !important" },
    "& .MuiTableCell-head": {
      background: `${isDark ? "rgba(255,255,255,0.03)" : tokens.cardBg} !important`,
      borderBottom: `1px solid ${tokens.border}`,
    },
    "& .MuiTableCell-head .MuiTypography-root": {
      color: `${tokens.textSecondary} !important`,
      fontWeight: 600,
      fontSize: "0.78rem",
    },
    "& .MuiTableCell-body": {
      borderBottom: `1px solid ${tokens.border}`,
      color: tokens.textPrimary,
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  };

  const { control, handleSubmit, reset, setError, watch, getValues, setValue, formState } =
    useForm<ProductItemPriceInfo>({ defaultValues: initialValuesOfProductItemPrice });
  const { errors } = formState;

  const validateAmount = (value: string, message: string) => {
    const val: number = value !== "" ? parseFloat(value) : 0;
    if (val === 0 || val === 0.0) {
      return message;
    }
  };

  const renderCurrencyField = (
    controllerName: string,
    rules?: object
  ) => (
    <Box sx={inputFieldSx()}>
      <CustomCurrencyField
        control={control}
        controllerName={controllerName}
        label=""
        size="small"
        showErrorMinLength={showInlineErrorMinLength}
        rules={rules}
        sx={{
          "& .MuiInputAdornment-root": { color: tokens.textSecondary },
        }}
      />
    </Box>
  );

  const errorMessages = useMemo(() => {
    const errorMsgs: JSX.Element[] = [];
    if (errors) {
      const objKeys: string[] = Object.keys(errors);
      let errMsg: string | undefined = undefined;
      if (objKeys && objKeys.length > 0) {
        objKeys.map((key) => {
          // @ts-ignore
          errMsg = errors[key].message;
          if (
            errMsg &&
            (errMsg.length >= showInlineErrorMinLength || errMsg.includes(customTopErrorString))
          ) {
            errMsg = errMsg.replace(customTopErrorString, "");
            errorMsgs.push(
              <Box sx={{ display: "flex", justifyContent: "flex-start", color: "error.main" }}>
                <Box sx={{ pr: 0.5 }}>
                  <ErrorOutlineRounded />
                </Box>
                <Box>{errMsg}</Box>
              </Box>
            );
          }
        });
      }
    }
    return errorMsgs;
  }, [formState]);

  const options = useMemo(() => {
    return [
      { label: "MAC", value: "MAC" },
      { label: "National", value: "NATIONAL" },
    ];
  }, []);

  const columnsPriceItem: Column[] = useMemo(() => {
    const columnArray: Column[] = [{ id: "sku", label: "SKU", minWidth: 100 }];
    columnArray.push(
      { id: "listPrice", label: "List Price", minWidth: 100, align: "center" },
      { id: "allowableUnitPrice", label: "Allowable", minWidth: 100, align: "center" },
      { id: "customPrice", label: "Custom Price", minWidth: 100, align: "center" }
    );
    return columnArray;
  }, []);

  const productItemPriceRows = useMemo(() => {
    return productItemsList?.map((row: any, index: number) => ({
      ...row,
      listPrice: renderCurrencyField(`listPrice${row.sku}-${index}`, {
        required: "List Price is required.",
        validate: {
          validateAmount: (value: string) => validateAmount(value, "Enter a valid List Price."),
        },
      }),
      allowableUnitPrice: renderCurrencyField(`allowableUnitPrice${row.sku}-${index}`, {
        required: "Price is required.",
        validate: {
          validateAmount: (value: string) => validateAmount(value, "Enter a valid Price."),
        },
      }),
      customPrice: renderCurrencyField(`customPrice${row.sku}-${index}`),
    }));
  }, [productItemsList, control, tokens.textSecondary]);

  useEffect(() => {
    if (open) {
      const transformedObject = productItemPriceObject.ProductItemPrices.reduce(
        (acc: any, item: any, index: number) => {
          const transformedItem = {
            [`allowableUnitPrice${item?.sku}-${index}`]: item?.allowableUnitPrice,
            [`customPrice${item?.sku}-${index}`]: item?.customPrice,
            [`listPrice${item?.sku}-${index}`]: item?.listPrice,
          };
          return { ...acc, ...transformedItem };
        },
        {}
      );

      if (productItemPriceObject?.ProductItemPrices[0]?.effectiveDate) {
        transformedObject.effectiveDate =
          productItemPriceObject?.ProductItemPrices[0]?.effectiveDate;
      }

      if (productItemPriceObject?.ProductItemPrices[0]?.inactiveDate) {
        transformedObject.inactiveDate =
          productItemPriceObject?.ProductItemPrices[0]?.inactiveDate;
      }

      reset(transformedObject);
    }
    if (!open) {
      setDefaultValues();
    }
  }, [open]);

  useEffect(() => {
    setValue("priceType", "NATIONAL");
  }, [setValue]);

  const validateOthers = useCallback(() => {
    if (inactiveOnly) {
      return validateInactivate();
    }

    if (!productItemPriceList || productItemPriceList?.length === 0) {
      return true;
    }

    let priceList: ProductItemPriceInfo[] = productItemPriceList;
    if (editPricing) {
      priceList = productItemPriceList.filter((_item: ProductItemPriceInfo, index: number) => {
        return index !== rowIndex;
      });
    }

    const fileType: string = getValues("priceType");
    const macId: number = getValues("macId");
    const effectiveDateLocal: Dayjs = dayjs(getValues("effectiveDate"));

    if (editProduct) {
      for (let i: number = 0; i < priceList.length; i++) {
        if (
          dayjs(priceList[i].inactiveDate).isValid() &&
          effectiveDateLocal.isValid() &&
          (effectiveDateLocal.isSame(dayjs(priceList[i].inactiveDate)) ||
            effectiveDateLocal.isBefore(dayjs(priceList[i].inactiveDate))) &&
          (fileType === "NATIONAL" ||
            priceList[i].priceType === "NATIONAL" ||
            macId === priceList[i].macId)
        ) {
          return (
            "Entered effective date is conflicted with a active period of a existing pricing." +
            customTopErrorString
          );
        }
      }
    } else {
      for (let i: number = 0; i < priceList.length; i++) {
        if ("MAC" === priceList[i].priceType && fileType === "NATIONAL") {
          erroredFields.push("priceType");
          return "Mac pricing is/are already exists." + customTopErrorString;
        } else if (fileType === priceList[i].priceType && fileType === "NATIONAL") {
          erroredFields.push("priceType");
          return "A National pricing is already exists." + customTopErrorString;
        } else if (macId === priceList[i].macId) {
          erroredFields.push("macId");
          return "This MAC pricing is already exists." + customTopErrorString;
        }
      }
    }
    return true;
  }, [productItemPriceList, watch("priceType"), watch("macId"), watch("effectiveDate")]);

  const validateInactivate = useCallback(() => {
    const fileType: string = getValues("priceType");
    const macId: number = getValues("macId");
    const effectiveDateLocal: Dayjs = dayjs(getValues("effectiveDate"));
    const inactiveDateLocal: Dayjs = dayjs(getValues("inactiveDate"));

    let priceList: ProductItemPriceInfo[] = productItemPriceList;
    if (editPricing) {
      priceList = productItemPriceList.filter((item: ProductItemPriceInfo) => {
        return effectiveDateLocal.isBefore(dayjs(item.effectiveDate));
      });
    }

    for (let i: number = 0; i < priceList.length; i++) {
      if (
        inactiveDateLocal.isValid() &&
        (inactiveDateLocal.isSame(dayjs(priceList[i].effectiveDate)) ||
          inactiveDateLocal.isAfter(dayjs(priceList[i].effectiveDate))) &&
        (fileType === "NATIONAL" ||
          priceList[i].priceType === "NATIONAL" ||
          macId === priceList[i].macId)
      ) {
        return (
          "Entered inactive date is conflicted with a active period of a existing pricing." +
          customTopErrorString
        );
      }
    }
  }, [productItemPriceList, watch("priceType"), watch("macId"), watch("inactiveDate")]);

  const validateEffectiveDate = useCallback(
    (value: string | number | Date | dayjs.Dayjs | null | undefined) => {
      if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
        return "Invalid date format.";
      }
      return true;
    },
    [productItemPriceList, watch("effectiveDate"), watch("macId")]
  );

  const validateInactiveDate = useCallback(
    (value: string | number | Date | dayjs.Dayjs | null | undefined) => {
      if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
        return "Invalid date format.";
      }

      if (
        dayjs(value, "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isAfter(
          dayjs(value, "MM/DD/YYYY", true)
        )
      ) {
        return "Inactive date should be a past date or euqal to effective date.";
      }

      return true;
    },
    [productItemPriceList, watch("inactiveDate"), watch("macId")]
  );

  const validateOptionalInactiveDate = useCallback(
    (value: string | number | Date | dayjs.Dayjs | null | undefined) => {
      if (
        dayjs(value, "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isAfter(
          dayjs(value, "MM/DD/YYYY", true)
        )
      ) {
        return "The inactive date should be the same as or after the effective date.";
      }

      return true;
    },
    [productItemPriceList, watch("inactiveDate"), watch("macId")]
  );

  const setDefaultValues = useCallback(() => {
    reset();
  }, []);

  const onSubmit = useCallback(
    (data: any) => {
      const normalizedData = Object.keys(data).reduce((acc: Record<string, any>, key) => {
        const normalizedKey = key.replace(/\s+/g, "");
        acc[normalizedKey] = data[key];
        return acc;
      }, {});

      const normalizedProductItemsList = productItemsList.reduce(
        (acc: Record<string, any>, item: any) => {
          const normalizedSku = item.sku.replace(/\s+/g, "");
          acc[normalizedSku] = item;
          return acc;
        },
        {}
      );

      const productItems = Object.keys(normalizedData)
        .filter((key) => key.match(/listPrice[\w\d-_.@]+-\d+/))
        .map((key) => {
          const match = key.match(/listPrice([\w\d-_.@]+)-(\d+)/);
          const sku = match?.[1];
          const index = match?.[2];
          const normalizedSku = sku?.replace(/\s+/g, "");
          //@ts-ignore
          const productItem = normalizedProductItemsList[normalizedSku];

          return {
            productItemId: productItem?.productItemId
              ? parseInt(productItem.productItemId, 10)
              : null,
            productPriceId: productItem?.productPriceId || null,
            sku: productItem?.sku || null,
            listPrice: normalizedData[`listPrice${sku}-${index}`],
            allowableUnitPrice: normalizedData[`allowableUnitPrice${sku}-${index}`],
            customPrice: normalizedData[`customPrice${sku}-${index}`] || "",
          };
        });

      const obj: { effectiveDate: string; inactiveDate: string; productItems: any[] } = {
        effectiveDate: normalizedData.effectiveDate
          ? dayjs(normalizedData.effectiveDate).format("MM/DD/YYYY")
          : "",
        inactiveDate: normalizedData.inactiveDate
          ? dayjs(normalizedData.inactiveDate).format("MM/DD/YYYY")
          : "",
        productItems,
      };

      addProductItemsPrice(obj);
    },
    [open]
  );

  const onError = (_errors: any) => {
    erroredFields.forEach((fieldName: string) => {
      // @ts-ignore
      setError(fieldName, {});
    });
    erroredFields = [];
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-describedby="product-items-price-dialog"
      onClick={(event) => event.stopPropagation()}
      PaperProps={{ sx: paperSx(isMobile) }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <DollarSign size={22} color={tokens.accent} strokeWidth={1.8} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              {editPricing ? "Update Product Pricing" : "Add Product Pricing"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              {inactiveOnly
                ? "Set an inactive date for this pricing."
                : "Enter pricing details and set prices for each product item."}
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={{ ...bodySx, minHeight: 0, overflowY: "auto" }}>
          {errorMessages.length > 0 && <Box sx={{ pb: 1 }}>{errorMessages}</Box>}

          {!inactiveOnly && (
            <>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <FieldLabel label="Effective Date" required tokens={tokens} />
                  <Box sx={datepickerWrapSx(!!errors.effectiveDate)}>
                    <CustomDatePicker
                      label=""
                      openTo="day"
                      controllerName="effectiveDate"
                      control={control}
                      size="small"
                      showErrorMinLength={showInlineErrorMinLength}
                      format="MM/DD/YYYY"
                      rules={{
                        required: "Effective Date is required.",
                        validate: {
                          isValidDate: (
                            value:
                              | string
                              | number
                              | Date
                              | dayjs.Dayjs
                              | null
                              | undefined
                          ) => validateEffectiveDate(value),
                        },
                      }}
                    />
                  </Box>
                </Grid>

                {editProduct && (
                  <Grid item md={4} xs={12}>
                    <FieldLabel label="Inactive Date" tokens={tokens} />
                    <Box sx={datepickerWrapSx(!!errors.inactiveDate)}>
                      <CustomDatePicker
                        label=""
                        openTo="day"
                        controllerName="inactiveDate"
                        control={control}
                        size="small"
                        showErrorMinLength={showInlineErrorMinLength}
                        format="MM/DD/YYYY"
                        rules={{
                          validate: {
                            isValidDate: (
                              value:
                                | string
                                | number
                                | Date
                                | dayjs.Dayjs
                                | null
                                | undefined
                            ) => validateOptionalInactiveDate(value),
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                )}

                <Grid item md={4} xs={12}>
                  <FieldLabel label="File Type" tokens={tokens} />
                  <Box sx={dropdownWrapSx(!!errors.priceType)}>
                    <CustomDropdownField
                      label=""
                      name="priceType"
                      options={options}
                      control={control}
                      size="small"
                      showErrorMinLength={showInlineErrorMinLength}
                      disabled={true}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 1 }}>
                <FieldLabel label="Product Item Prices" tokens={tokens} />
                <CustomTable
                  columns={columnsPriceItem}
                  rows={productItemPriceRows || []}
                  disablePagination={true}
                  emptyText="No Product Item(s) Found"
                  sx={modalTableSx}
                />
              </Box>
            </>
          )}

          {watch("productPriceId") && inactiveOnly && (
            <Grid container spacing={2}>
              <Grid item md={8} xs={12}>
                <FieldLabel label="Inactive Date" required tokens={tokens} />
                <Box sx={datepickerWrapSx(!!errors.inactiveDate)}>
                  <CustomDatePicker
                    label=""
                    openTo="day"
                    controllerName="inactiveDate"
                    control={control}
                    size="small"
                    showErrorMinLength={showInlineErrorMinLength}
                    format="MM/DD/YYYY"
                    rules={{
                      validate: {
                        isValidDate: (
                          value:
                            | string
                            | number
                            | Date
                            | dayjs.Dayjs
                            | null
                            | undefined
                        ) => validateInactiveDate(value),
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              startIcon={<EditOutlined sx={{ fontSize: "1rem !important" }} />}
              sx={primaryBtnSx}
            >
              {editPricing ? "Update Price Item" : "Add Price Item"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default ProductItemsPricePopup;
